import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useGetAccountInfo, useGetNetworkConfig } from '@multiversx/sdk-dapp/hooks';
import { sendTransactions } from '@multiversx/sdk-dapp/services';
import { ProxyNetworkProvider } from '@multiversx/sdk-network-providers';
import { Account, Address, AddressValue, ContractFunction, Interaction, ResultsParser, SmartContract, SmartContractAbi, TokenPayment, TypedValue } from '@multiversx/sdk-core/out';
import axios from 'axios';
import BigNumber from 'bignumber.js/bignumber';
import { contracts, ElrondApiUrl, isDev } from 'config/config';
import { BigZero, coinNames } from 'helpers/constant';
import { convertWeiToEsdt } from 'helpers/utils';

import {
    FTPayment,
    NFTPayment,
    TokenInfoArrayType
} from 'types';
import { useLocation } from 'react-router-dom';
import { routeNames } from 'routes';

export type TCallMethodProperties = {
    contract: any,
    method: string,
    args?: TypedValue[],
    egld?: BigNumber.Value;
    fts?: FTPayment[];
    nfts?: NFTPayment[];
    wrap?: BigNumber.Value,
    // unwrap?: BigNumber.Value
};

export type TViewMethodProperties = {
    contract: any,
    method: string,
    args?: TypedValue[],
};

export type TCallMethodWithMultiProperties = {
    callerAddress?: string;
    nft?: string;
    nonce?: number;
} & TViewMethodProperties;

type TViewMethod = (values: TViewMethodProperties) => Promise<any>;

type TCallMethod = (values: TCallMethodProperties) => Promise<any>;


interface IInteractionContext {
    egldPrice: number;
    usdcPrice: number;
    
    pairInfos: any[];
    userPairInfos: any[];
    activePairInfos: any[];
    pairTokens: string[];

    farmInfos: any[];
    userFarmInfos: any[];

    dualFarmInfos: any[];
    userDualFarmInfos: any[];
    
    stakingInfos: any[];
    userStakingInfos: any[];

    swapPaths: string[][];
    pairTokenInfos: TokenInfoArrayType;
    viewMethod: TViewMethod;
    callMethod: TCallMethod;
    networkProvider: ProxyNetworkProvider;
    fetching: boolean;
}

const InteractionContext = createContext<IInteractionContext>({} as IInteractionContext);

const InteractionProvider = ({ children }: { children: any }) => {
    const [pairInfos, setPairInfos] = useState<any[]>([]);
    const [userPairInfos, setUserPairInfos] = useState<any[]>([]);
    const [activePairInfos, setActivePairInfos] = useState<any[]>([]);
    const [pairTokens, setPairTokens] = useState<string[]>([]);
    const [pairTokenInfos, setPairTokenInfos] = useState<TokenInfoArrayType>({});

    const [farmInfos, setFarmInfos] = useState<any[]>([]);
    const [userFarmInfos, setUserFarmInfos] = useState<any[]>([]);

    const [dualFarmInfos, setDualFarmInfos] = useState<any[]>([]);
    const [userDualFarmInfos, setUserDualFarmInfos] = useState<any[]>([]);

    const [stakingInfos, setStakingInfos] = useState<any[]>([]);
    const [userStakingInfos, setUserStakingInfos] = useState<any[]>([]);

    const [swapPaths, setSwapPaths] = useState<string[][]>([]);
    const [egldPrice, setEgldPrice] = useState(0);
    const [usdcPrice, setUsdcPrice] = useState(0);

    const { account: {address: userAddress, balance: userBalance, shard} } = useGetAccountInfo();
    const { network: { apiAddress } } = useGetNetworkConfig();
    const [fetching, setFetching] = useState(false);

    const { pathname } = useLocation();

    const networkProvider = new ProxyNetworkProvider(apiAddress, { timeout: 10000 });

    useEffect(() => {
        getInfos();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            getInfos();
        }, 6000);
        
        return () => clearTimeout(timer);
    }, [fetching]);

    useEffect(() => {
        let paths: string[][] = [];
        const depth_1_paths: string[][] = [];
        const depth_2_paths: string[][] = [];
        const depth_3_paths: string[][] = [];
        const depth_4_paths: string[][] = [];

        // depth 1
        for(const pair of activePairInfos) {
            depth_1_paths.push([pair.first_token_id, pair.second_token_id]);
            depth_1_paths.push([pair.second_token_id, pair.first_token_id]);
        }

        // depth 2
        for(const first_path of depth_1_paths) {
            for(const second_path of depth_1_paths) {
                if(first_path[1] === second_path[0] && first_path[0] !== second_path[1]) {
                    depth_2_paths.push(first_path.concat([second_path[1]]));
                }
            }
        }

        // depth 3
        for(const first_path of depth_2_paths) {
            for(const second_path of depth_1_paths) {
                if(
                    first_path[2] === second_path[0] &&
                    first_path[0] !== second_path[1] &&
                    first_path[1] !== second_path[1]
                ) {
                    depth_3_paths.push(first_path.concat([second_path[1]]));
                }
            }
        }

        // depth 4
        for(const first_path of depth_3_paths) {
            for(const second_path of depth_1_paths) {
                if(
                    first_path[3] === second_path[0] &&
                    first_path[0] !== second_path[1] &&
                    first_path[1] !== second_path[1] &&
                    first_path[2] !== second_path[1]
                ) {
                    depth_4_paths.push(first_path.concat([second_path[1]]));
                }
            }
        }

        paths = paths.concat(depth_1_paths);
        paths = paths.concat(depth_2_paths);
        paths = paths.concat(depth_3_paths);
        paths = paths.concat(depth_4_paths);

        setSwapPaths(paths);
    }, [activePairInfos]);

    const sleep = (s: number) => {
        return new Promise(resolve => setTimeout(resolve, s * 1000));
    };

    const getInfos = () => {
        (async () => {
            setFetching(true);

            try {
                const wegldInfo = await axios.get(`${ElrondApiUrl}/tokens/${coinNames[1]}`);
                
                if(!isNaN(wegldInfo.data.price)) {
                    setEgldPrice(wegldInfo.data.price);
                }

                await sleep(0.5);
                
                const usdcInfo = await axios.get(`${ElrondApiUrl}/tokens/${coinNames[2]}`);
                
                if(!isNaN(usdcInfo.data.price)) {
                    setUsdcPrice(usdcInfo.data.price);
                }

            } catch(error) {
                console.log(error);
            }

            await sleep(0.5);

            try {
                const pairs = await viewMethod({
                    contract: contracts.Dex,
                    method: 'viewPairs'
                });
                
                const allPairs = [];
                const userPairs = [];
                const activePairs = [];

                const availableTokens: string[] = [];
                const allTokens: string[] = [];
                const tokenInfos: TokenInfoArrayType = {};

                availableTokens.push('EGLD');

                tokenInfos['EGLD'] = {
                    identifier: 'EGLD',
                    balance: convertWeiToEsdt(userAddress ? userBalance : 0, 18),
                    decimal: 18
                };

                for(const pair of pairs) {
                    const first_token_id = pair.first_token_id;
                    const first_token_decimal = pair.first_token_decimal.toNumber();
                    const second_token_id = pair.second_token_id;
                    const second_token_decimal = pair.second_token_decimal.toNumber();
                    const lp_token_id = pair.lp_token_id;

                    // console.log(pair.pair_id.toNumber(), pair);

                    if(!allTokens.includes(first_token_id)) {
                        allTokens.push(first_token_id);
                        availableTokens.push(first_token_id);

                        tokenInfos[first_token_id] = {
                            identifier: first_token_id,
                            balance: BigZero,
                            decimal: first_token_decimal
                        };
                    }

                    if(!allTokens.includes(second_token_id)) {
                        allTokens.push(second_token_id);
                        availableTokens.push(second_token_id);

                        tokenInfos[second_token_id] = {
                            identifier: second_token_id,
                            balance: BigZero,
                            decimal: second_token_decimal
                        };
                    }

                    if(lp_token_id && !allTokens.includes(lp_token_id)) {
                        allTokens.push(lp_token_id);

                        tokenInfos[lp_token_id] = {
                            identifier: lp_token_id,
                            balance: BigZero,
                            decimal: 18
                        };
                    }
                }

                if(allTokens.length && userAddress) {
                    try {
                        const { data } = await axios.get(`${ElrondApiUrl}/accounts/${userAddress}/tokens?identifiers=${allTokens.join(',')}`);

                        for(const token of data) {
                            const token_id: string = token.identifier;
                            const decimal = token.decimals;
                            const balance = new BigNumber(token.balance || 0);

                            tokenInfos[token_id] = {
                                identifier: token_id,
                                balance: convertWeiToEsdt(balance, decimal),
                                decimal: decimal
                            };
                        }
                    } catch (error) {
                        console.log(error);
                    }
                }

                // console.log(pairs);

                for(const pair of pairs) {
                    const first_token_id = pair.first_token_id;
                    const second_token_id = pair.second_token_id;
                    const lp_token_id = pair.lp_token_id;


                    allPairs.push({
                        ...pair,
                        first_token_balance: tokenInfos[first_token_id].balance,
                        second_token_balance: tokenInfos[second_token_id].balance,
                        lp_token_balance: lp_token_id ? tokenInfos[lp_token_id].balance : BigZero,
                    });
                }

                for(const pair of allPairs) {
                    if(pair.state.name === 'Active') {
                        activePairs.push(pair);
                    }
                }

                if(userAddress) {
                    for(const pair of allPairs) {
                        if(pair.owner.toString() === userAddress) {
                            userPairs.push(pair);
                        }
                    }
                }

                setPairInfos(allPairs);
                setUserPairInfos(userPairs);
                setActivePairInfos(activePairs);
                setPairTokens(availableTokens);
                setPairTokenInfos(tokenInfos);
            } catch (error) {
                console.log(error);
            }

            if(
                pathname == routeNames.farm || pathname == routeNames.createFarm ||
                pathname == routeNames.dualfarm || pathname == routeNames.createDualFarm ||
                (!farmInfos.length && !dualFarmInfos.length)
            ) {
                try {
                    const farms = await viewMethod({
                        contract: contracts.Farm,
                        method: 'viewPools',
                        args: userAddress ? [
                            new AddressValue(new Address(userAddress))
                        ] : []
                    });
    
                    const singleFarms = [];
                    const userFarms = [];
                    const dualFarms = [];
                    const userDualFarms = [];
    
                    for(const farm of farms) {
                        if(farm.is_dual) {
                            dualFarms.push(farm);
                            if(farm.owner.toString() === userAddress) {
                                userDualFarms.push(farm);
                            }
                        } else {
                            singleFarms.push(farm);
                            if(farm.owner.toString() === userAddress) {
                                userFarms.push(farm);
                            }
                        }
                    }
    
                    setFarmInfos(singleFarms);
                    setUserFarmInfos(userFarms);

                    setDualFarmInfos(dualFarms);
                    setUserDualFarmInfos(userDualFarms);
                } catch (error) {
                    console.log(error);
                }
            }

            if(pathname == routeNames.staking || pathname == routeNames.createStaking || !stakingInfos.length) {
                try {
                    const stakings = await viewMethod({
                        contract: contracts.Staking,
                        method: 'viewPools',
                        args: userAddress ? [
                            new AddressValue(new Address(userAddress))
                        ] : []
                    });
    
                    const userStakings = [];
    
                    for(const staking of stakings) {
                        if(staking.owner.toString() === userAddress) {
                            userStakings.push(staking);
                        }
                    }
    
                    setStakingInfos(stakings);
                    setUserStakingInfos(userStakings);
                } catch (error) {
                    console.log(error);
                }
            }


            setFetching(false);
        })();
    };

    const viewMethod = useCallback<TViewMethod>(async ({
        contract,
        method,
        args = []
    }: {
        contract: any,
        method: string,
        args?: any[]
    }): Promise<any> => {
        const { address, abi } = contract;

        const contractAddress = new Address(address);
        const contractAbi = new SmartContractAbi(abi);
        const smartContract = new SmartContract({ address: contractAddress, abi: contractAbi });

        const query = smartContract.createQuery({
            func: new ContractFunction(method),
            args,
        });

        const queryResponse = await networkProvider.queryContract(query);
    
        const resultsParser = new ResultsParser();
        const endpointDefinition = smartContract.getEndpoint(method);
    
        const { firstValue } = resultsParser.parseQueryResponse(queryResponse, endpointDefinition);

        return firstValue?.valueOf();
    }, [userAddress]);
    
    const callMethod = useCallback<TCallMethod>(async ({
        contract,
        method,
        args = [],
        egld = BigZero,
        fts = [],
        nfts = [],
        wrap = BigZero,
        // unwrap = BigZero
    }) => {
        if(userAddress) {
            const { address, abi } = contract;

            const contractAddress = new Address(address);
            const contractAbi = new SmartContractAbi(abi);
            const smartContract = new SmartContract({ address: contractAddress, abi: contractAbi });

            const userAccount = new Account(new Address(userAddress));

            const updatedUserAccount = await networkProvider.getAccount(
                new Address(userAddress)
            );

            userAccount.update(updatedUserAccount);
            
            const transactions = [];

            if(BigZero.lt(wrap)) {
                let wrapAddress = new Address(contracts.Wrap0.address);
                
                if(shard === 1) {
                    wrapAddress = new Address(contracts.Wrap1.address);
                } else if (shard === 2) {
                    wrapAddress = new Address(contracts.Wrap2.address);
                }
                const wrapContract = new SmartContract({ address: wrapAddress });
                const wrapFunction = new ContractFunction('wrapEgld');
                const wrapInteraction = new Interaction(wrapContract, wrapFunction, []);

                const wrapTx = wrapInteraction
                    .withNonce(userAccount.nonce)
                    .withValue(TokenPayment.egldFromAmount(wrap))
                    .withGasLimit(20000000)
                    .withChainID(isDev ? 'D' : '1')
                    .buildTransaction();

                transactions.push(wrapTx);
            }

            const tx = smartContract.methodsExplicit[method](args)
                            .withNonce(BigZero.lt(wrap) ? userAccount.nonce.valueOf() + 1 : userAccount.nonce)
                            .withGasLimit(90000000)
                            .withChainID(isDev ? 'D' : '1');
            
            if(BigZero.lt(egld)) {
                tx.withValue(TokenPayment.egldFromAmount(egld));
            }

            const esdtPayment = [];
            for(const ft of fts) {
                esdtPayment.push(TokenPayment.fungibleFromAmount(ft.token, ft.amount, ft.decimals));
            }
            for(const nft of nfts) {
                esdtPayment.push(TokenPayment.nonFungible(nft.token, nft.nonce));
            }
            if(esdtPayment.length === 1) {
                if(fts.length === 1) {
                    tx.withSingleESDTTransfer(esdtPayment[0]);
                } else {
                    tx.withSingleESDTNFTTransfer(esdtPayment[0], userAccount.address);
                }
            } else if(esdtPayment.length > 1) {
                tx.withMultiESDTNFTTransfer(esdtPayment, userAccount.address);
            }

            const transaction = tx.buildTransaction();

            transactions.push(transaction);

            // console.log(transactions);

            await sendTransactions({
                transactions: transactions,
                transactionsDisplayInfo: {
                  processingMessage: 'Processing Transaction',
                  errorMessage: 'Transaction Failed',
                  successMessage: 'Transaction Success',
                },
                redirectAfterSign: false,
            });
        }       
    
        return '';
    }, [userAddress]);

    const values = useMemo(() => (
        {
            fetching, egldPrice, usdcPrice, pairInfos, activePairInfos, userPairInfos, pairTokens, pairTokenInfos, swapPaths, farmInfos, userFarmInfos, dualFarmInfos, userDualFarmInfos, stakingInfos, userStakingInfos,
            viewMethod, callMethod, networkProvider
        }
    ), [
        fetching, egldPrice, usdcPrice, pairInfos, activePairInfos, userPairInfos, pairTokens, pairTokenInfos, swapPaths, farmInfos, userFarmInfos, dualFarmInfos, userDualFarmInfos, stakingInfos, userStakingInfos,
        viewMethod, callMethod, networkProvider
    ]);

    return (
        <InteractionContext.Provider value={values}>
            {children}
        </InteractionContext.Provider>
    );
};

const useInteraction = () => useContext(InteractionContext);

export { useInteraction, InteractionProvider };